import styled from 'styled-components';
import TextModal from '@/components/modal/TextModal';
import TopNavigation from '@/components/xrNavi/TopNavigation';
import { NormalizedPin } from '@/modules/defines/interface';
import { useModal } from '@lasbe/react-modal';
import { useState, useEffect, Fragment, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import usePanoramaStore from '@/store/panorama';
import useEquirectangularStore from '@/store/viewer';
import EquirectangularViewer from '@/components/viewer/scene/EquirectangularView';
import CameraMovement from '@/components/viewer/camera/CameraMovement';
import ResponsivePositionMarker from '@/components/viewer/scene/ResponsivePositionMarker';
import { RaycastProvider } from '@/components/viewer/scene/RaycastManager';
import { CameraControlDirectionDrag } from '@/components/viewer/camera/CameraControlDirectionDrag';
import { Panorama } from '@/modules/defines/interface';
import { calculatePosition } from '@/utils/function/util';
import { useSpring, animated } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import TransitPin from '@/assets/icon/minimap/TransitPin';
import getZoneMap from '@/utils/function/getZoneMap';
import { mapOptions } from '@/modules/defines/datas';
import NavigationMarker from '@/components/navi/NavigationMarker';
import map_start_pin from '@/assets/icon/map-pin-start.svg';
import map_end_pin from '@/assets/icon/map-pin-end.svg';
import { Globals } from '@react-spring/three';
import LoadingModal from '@/components/modal/LoadingModal';
import { logArrive, logPageView } from '@/utils/amplitude';
import useFeedbackModal from '@/utils/hook/useProbabilityModal';

const ZOOM_LEVEL = 0.75;
const INITIAL_X = -200;
const INITIAL_Y = -350;
const ZOOM_SENSITIVITY_MOBILE = 0.2;
const ZOOM_SENSITIVITY_DESKTOP = 0.2;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 3;
const XR_NAVI_WORKER_URL = `${import.meta.env.BASE_URL}worker.js`;

const XRNaviContainer = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [startPoint, setStartPoint] = useState<NormalizedPin | null>(null);
  const [endPoint, setEndPoint] = useState<NormalizedPin | null>(null);
  const [reachable, setReachable] = useState(true);
  const [path, setPath] = useState<Panorama[]>([]);
  const [pathId, setPathId] = useState<number[]>([]);
  const [dataView, setDataView] = useState<Panorama[]>([]);
  const [zoneState, setZoneState] = useState<string>('1F_atti');

  const initializeAfterAllLoaded = useEquirectangularStore(
    (state) => state.initializeAfterAllLoaded
  );
  // const currentViewerKey = useEquirectangularStore(
  //   (state) => state.currentViewerKey
  // );
  const [initialized, setInitialized] = useState(false);
  const [loadedComponents, setLoadedComponents] = useState(0);
  const handleComponentLoad = () => {
    setLoadedComponents((prevCount) => prevCount + 1);
  };
  // const movingPosition = usePanoramaStore((state) => state.movingPosition);
  // const setMovingPosition = usePanoramaStore(
  //   (state) => state.setMovingPosition
  // );
  const currentPosition = usePanoramaStore((state) => state.currentPosition);
  const setCurrentPosition = usePanoramaStore(
    (state) => state.setCurrentPosition
  );
  const setHotspotButtonData = usePanoramaStore(
    (state) => state.setHotspotButtonData
  );
  const spaceId = usePanoramaStore((state) => state.spaceId);
  const setSpaceId = usePanoramaStore((state) => state.setSpaceId);
  const pid = usePanoramaStore((state) => state.id);
  const setPid = usePanoramaStore((state) => state.setId);
  const setMoveByWidget = usePanoramaStore((state) => state.setMoveByWidget);
  const { showModal } = useFeedbackModal();
  const hasShownModalRef = useRef(false);

  const navigate = useNavigate();
  const {
    openModal: openInvalidRouteModal,
    closeModal: closeInvalidRouteModal,
  } = useModal();

  const projectionMapping = (point: Panorama) => {
    return { x: point.x_img * 100 + 900, y: -point.y_img * 100 + 700 };
  };

  const [{ x, y, scale }, api] = useSpring(() => ({
    x: INITIAL_X,
    y: INITIAL_Y,
    scale: ZOOM_LEVEL,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
    },
  }));

  const handleMarkerClick = (
    panoId: number,
    x: number,
    y: number,
    floor: number
  ) => {
    setMoveByWidget(true);
    setPid(panoId);
    setCurrentPosition([y, floor, x]);
  };

  useEffect(() => {
    const isPoint = sessionStorage.getItem('xr-navi-point');
    const route_result = sessionStorage.getItem('xr-navi-route');
    if (route_result) {
      const result = JSON.parse(route_result);
      setReachable(result.reachable);

      if (result.panorama_path) {
        result.panorama_path.forEach((p: any, index: number) => {
          if (index < result.panorama_path.length - 1) {
            const nextItem = result.panorama_path[index + 1]; // 다음 요소를 가져옵니다.

            // p와 p의 다음 요소의 space_id가 같으면
            if (nextItem && p.space_id === nextItem.space_id) {
              // 다음 요소의 좌표를 result에 추가
              p.coordToLook = [nextItem.y, nextItem.floor, nextItem.x];
            } else if (p.externalList && p.externalList.length > 0) {
              // p의 externalList에서 데이터를 꺼내와야 하는 경우
              p.externalList.map((external: any) => {
                const temp_data = external.split('_');
                if (nextItem.id === parseInt(temp_data[1])) {
                  const connectPosition = calculatePosition(
                    parseFloat(temp_data[2]),
                    [p.y, p.floor, p.x]
                  );
                  p.coordToLook = connectPosition;
                }
              });
            }
          } else {
            p.coordToLook = [0, 0, 0];
          }
        });
        setPath(result.panorama_path);
      }
      setPathId(result.path);
    } else {
      setReachable(false);
    }

    if (isPoint) {
      const parsedPoint = JSON.parse(isPoint);
      setStartPoint(parsedPoint.dep);
      setEndPoint(parsedPoint.arr);
    }
  }, []);

  useEffect(() => {
    if (!reachable) {
      openInvalidRouteModal({
        content: (
          <TextModal
            height="12.5rem"
            type="alert"
            title="경로가 올바르지 않습니다."
            confirmText="확인"
            description={
              <Fragment>
                출발지 또는 도착지 경로가 올바르지 않습니다. <br />
                다시 한번 확인 해주세요.
              </Fragment>
            }
            handleConfirm={() => {
              closeInvalidRouteModal();
              navigate('/main');
            }}
          />
        ),
      });
    } else {
      if (path.length > 0) {
        setPid(path[0].id);
        setSpaceId(path[0].space_id);
        setCurrentPosition([path[0].y, path[0].floor, path[0].x]);
      }
    }
  }, [reachable, path]);

  useEffect(() => {
    if (pid && endPoint) {
      if (pid === endPoint.panorama_id) {
        logArrive();
        openInvalidRouteModal({
          content: (
            <TextModal
              height="12.5rem"
              type="alert"
              title="목적지 도착"
              confirmText="확인"
              description={
                <Fragment>VR 길찾기를 통해 목적지에 도착했습니다.</Fragment>
              }
              handleConfirm={() => {
                const storage = localStorage.getItem('isFeedback');
                if (storage === 'true') {
                  closeInvalidRouteModal();
                } else {
                  showModal(100);
                }
              }}
            />
          ),
        });
      }
    }
  }, [pid]);

  useEffect(() => {
    if (dataView.length > 0) {
      if (loadedComponents === dataView.length) {
        initializeAfterAllLoaded();
        setInitialized(true);
      }
    }
  }, [dataView, loadedComponents, initializeAfterAllLoaded]);

  useEffect(() => {
    if (path.length > 0 && initialized) {
      if (pid !== 0) {
        const tempData = path.filter((render: any) => render.id === pid)[0];
        if (spaceId !== tempData.space_id) {
          setSpaceId(tempData.space_id);
          handleMarkerClick(pid, tempData.x, tempData.y, tempData.floor);
        }
        const { x: x_target, y: y_target } = projectionMapping(tempData);
        const { width, height } = containerRef.current!.getBoundingClientRect();
        const targetX = (width / 2 - x_target) * ZOOM_LEVEL;
        const targetY = (height / 2 - y_target) * ZOOM_LEVEL;

        api.start({
          scale: ZOOM_LEVEL,
          x: targetX,
          y: targetY - height / (2 * ZOOM_LEVEL),
        });
      }
    }
  }, [path, pid, initialized]);

  useEffect(() => {
    if (path.length > 0) {
      const worker = new Worker(XR_NAVI_WORKER_URL);

      worker.onmessage = (e) => {
        const { updatedView } = e.data;

        setInitialized(false);
        setLoadedComponents(0);
        setDataView(updatedView);
        const matchedFloor = mapOptions.find(
          (option) => option.walkway_id === spaceId
        )?.id;
        setZoneState(matchedFloor!);
      };

      worker.postMessage({
        action: 'transformData',
        data: {
          path,
          spaceId,
        },
      });

      return () => {
        worker.terminate();
      };
    }
  }, [path, spaceId]);

  useEffect(() => {
    const worker = new Worker(XR_NAVI_WORKER_URL);
    if (initialized) {
      worker.postMessage({
        action: 'updateHotspotButton',
        data: {
          panorama: dataView,
          currentIndex: currentPosition,
        },
      });

      // 워커로부터 결과 받기
      worker.onmessage = (e) => {
        const { updatedHotspot } = e.data;
        setHotspotButtonData(updatedHotspot);
      };
    }

    // 컴포넌트가 언마운트되거나 초기화되지 않았을 때 워커 종료
    return () => {
      worker.terminate();
    };
  }, [initialized, currentPosition, dataView]);

  const isZoomingRef = useRef(false);
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dragStartRef = useRef<[number, number] | null>(null);
  const pinchStartScaleRef = useRef(1);
  const lastEmittedScale = useRef(1);
  const SCALE_THRESHOLD = 0.07;

  useGesture(
    {
      onDrag: ({
        pinching,
        cancel,
        offset: [ox, oy],
        velocity: [vx, vy],
        direction: [dirX, dirY],
        touches,
        last,
        memo,
      }) => {
        if (!containerRef.current) return;
        if (pinching) return cancel();

        api.stop();
        const currentScale = scale.get();
        const currentX = x.get();
        const currentY = y.get();
        console.log(currentX, currentY);

        if (!memo) {
          memo = [ox, oy, currentX, currentY];
        }

        const [startOx, startOy, startX, startY] = memo;
        const deltaX = (ox - startOx) * currentScale;
        const deltaY = (oy - startOy) * currentScale;

        if (touches === 1) {
          if (last) {
            const inertiaX = vx * dirX * 200;
            const inertiaY = vy * dirY * 200;
            api.start({
              x: startX + deltaX + inertiaX,
              y: startY + deltaY + inertiaY,
              config: { mass: 1, tension: 170, friction: 30 },
            });
          } else {
            api.start({
              x: startX + deltaX,
              y: startY + deltaY,
              immediate: true,
            });
          }
        }

        return memo;
      },

      onWheel: ({ event }) => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const currentScale = scale.get();
        const currentX = x.get();
        const currentY = y.get();

        const zoomFactor =
          event.deltaY > 0
            ? 1 - ZOOM_SENSITIVITY_DESKTOP
            : 1 + ZOOM_SENSITIVITY_DESKTOP;

        const newScale = Math.max(
          ZOOM_MIN,
          Math.min(ZOOM_MAX, currentScale * zoomFactor)
        );
        const tempX = INITIAL_X + event.clientX - centerX;
        const tempY = INITIAL_Y + event.clientY - centerY;

        const biasX =
          ((tempX - currentX) * newScale) / currentScale - (tempX - currentX);
        const biasY =
          ((tempY - currentY) * newScale) / currentScale - (tempY - currentY);

        const nextX = currentX - biasX;
        const nextY = currentY - biasY;

        isZoomingRef.current = true;
        if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
        zoomTimeoutRef.current = setTimeout(() => {
          isZoomingRef.current = false;
        }, 100);

        api.start({
          scale: newScale,
          x: nextX,
          y: nextY,
          config: { mass: 1, tension: 170, friction: 30 },
        });
      },
      onPinchStart: ({ offset: [s] }) => {
        pinchStartScaleRef.current = s || 1;
        lastEmittedScale.current = s || 1;
      },
      onPinchEnd: () => {
        dragStartRef.current = null;
      },
      onPinch: ({ offset: [s], origin: [ox, oy] }) => {
        if (!containerRef.current) return;

        const isMobile = document.body.classList.contains('isMobile');
        const prevScale = lastEmittedScale.current;
        const diff = Math.abs(s - prevScale);
        if (!isMobile && diff < SCALE_THRESHOLD) return;
        lastEmittedScale.current = s;

        const newScale = Math.max(
          ZOOM_MIN,
          Math.min(ZOOM_MAX, s * ZOOM_SENSITIVITY_MOBILE)
        );
        const currentX = x.get();
        const currentY = y.get();
        const currentScale = scale.get();

        const rect = containerRef.current.getBoundingClientRect();
        const containerWidth = rect.width;
        const containerHeight = rect.height;
        const centerX = rect.left + containerWidth / 2;
        const centerY = rect.top + containerHeight / 2;

        const tempX = INITIAL_X + ox - centerX;
        const tempY = INITIAL_Y + oy - centerY;

        const biasX =
          ((tempX - currentX) * newScale) / currentScale - (tempX - currentX);
        const biasY =
          ((tempY - currentY) * newScale) / currentScale - (tempY - currentY);

        const nextX = currentX - biasX;
        const nextY = currentY - biasY;

        api.start({
          scale: newScale,
          x: nextX,
          y: nextY,
        });
      },
    },
    {
      target: containerRef,
      drag: { threshold: 6 },
      pinch: { rubberband: false },
      wheel: { eventOptions: { passive: false } },
    }
  );

  useEffect(() => {
    logPageView('/xr-navi');

    // 3D 공간이 있는 B 페이지에 들어올 때 'demand'로 변경
    Globals.assign({ frameLoop: 'demand' });

    return () => {
      // A 페이지로 돌아갈 때 'always'로 다시 변경
      Globals.assign({ frameLoop: 'always' });
    };
  }, []);

  if (!startPoint || !endPoint) {
    return <LoadingModal isLoading={true} text="로딩 중" />;
  }

  return (
    <>
      <TopNavigation
        depTitle={startPoint.space_title}
        arrTitle={endPoint.space_title}
      />
      {pid !== 0 && (
        <Canvas
          camera={{
            position: currentPosition,
            fov: 90,
            rotation: new THREE.Euler(0, -Math.PI / 2, 0, 'YXZ'),
          }}
          style={{
            width: '100%',
            maxWidth: '100%',
            height: '70dvh',
          }}
        >
          <RaycastProvider>
            {dataView.length > 0 &&
              dataView.map(
                (item, index) =>
                  item.img_original && (
                    <Fragment key={`group-${item.id}`}>
                      {!item.blockedIdList.includes(pid) &&
                        !(
                          item.x === currentPosition[2] &&
                          item.y === currentPosition[0] &&
                          item.floor === currentPosition[1]
                        ) &&
                        item.floor === currentPosition[1] && (
                          <ResponsivePositionMarker
                            viewerKey={`viewer-${item.id}`}
                            position={[item.y, item.floor - 0.2, item.x]}
                            style={{ zIndex: 1 }}
                            panorama={item}
                            pathId={pathId}
                          />
                        )}

                      {item.x === currentPosition[2] &&
                        item.y === currentPosition[0] &&
                        item.floor === currentPosition[1] &&
                        item.connectedList.length > 0 &&
                        item.connectedList.map((connect) => {
                          if (pathId.includes(Number(connect.split('_')[0]))) {
                            const connectPosition = calculatePosition(
                              parseFloat(connect.split('_')[1]),
                              currentPosition
                            );
                            return (
                              <ResponsivePositionMarker
                                key={connect}
                                viewerKey={`viewer-${connect.split('_')[0]}`}
                                position={connectPosition}
                                style={{ zIndex: 1 }}
                                connect
                              />
                            );
                          }
                        })}
                      {pid === item.id &&
                        item.x === currentPosition[2] &&
                        item.y === currentPosition[0] &&
                        item.floor === currentPosition[1] &&
                        item.externalList.length > 0 &&
                        item.externalList.map((external) => {
                          const temp_data = external.split('_');
                          if (pathId.includes(Number(temp_data[1]))) {
                            const connectPosition = calculatePosition(
                              parseFloat(temp_data[2]),
                              currentPosition
                            );
                            const arrPosition = path.find(
                              (view) => view.id === parseInt(temp_data[1])
                            );
                            const buttonType =
                              item.category !== 'elevator' &&
                              item.category !== 'escalator'
                                ? 'exit'
                                : item.category === arrPosition!.category
                                ? item.category
                                : 'exit';
                            return (
                              <ResponsivePositionMarker
                                key={external}
                                viewerKey={external}
                                position={connectPosition}
                                style={{ zIndex: 1 }}
                                buttonText={external.split('_')[3]}
                                external
                                externalFunction={(key: string) => {
                                  const linkInfo = key.split('_');
                                  setPid(parseInt(linkInfo[1]));
                                }}
                                buttonType={buttonType}
                              />
                            );
                          }
                        })}
                      <EquirectangularViewer
                        viewerKey={`viewer-${item.id}`}
                        imageUrl={item.img_original}
                        imageThumbnail={item.img_thumbnail}
                        position={[item.y, item.floor, item.x]}
                        panorama={item}
                        onLoad={handleComponentLoad}
                        initialAngle={
                          item.direction !== 0
                            ? item.direction
                            : item.global_direction
                        }
                        coordToLook={
                          path.find((p) => p.id === item.id)?.coordToLook
                        }
                      />
                    </Fragment>
                  )
              )}
          </RaycastProvider>
          <CameraControlDirectionDrag />
          <CameraMovement />
        </Canvas>
      )}
      <MapContainer ref={containerRef}>
        <animated.div
          className="image-wrapper"
          style={{
            x,
            y,
            scale,
            touchAction: 'none',
            userSelect: 'none',
            position: 'relative',
          }}
        >
          <img
            src={getZoneMap(zoneState)}
            alt="Interactive"
            className="image"
            draggable="false"
            style={{
              position: 'relative',
            }}
          />

          {startPoint && startPoint.zoneState === zoneState && (
            <CurrentPinMarker
              src={map_start_pin}
              draggable={false}
              style={{
                position: 'absolute',
                left: `${startPoint.x}px`,
                top: `${startPoint.y}px`,
              }}
            />
          )}
          {endPoint && endPoint.zoneState === zoneState && (
            <CurrentPinMarker
              src={map_end_pin}
              draggable={false}
              style={{
                position: 'absolute',
                left: `${endPoint.x}px`,
                top: `${endPoint.y}px`,
              }}
            />
          )}
          {path && (
            <>
              <svg
                key={'path'}
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  left: 0,
                  top: 0,
                  position: 'absolute',
                  width: '1800px',
                  height: '1400px',
                }}
              >
                {path.map((data: any, index: number) => {
                  if (index === path.length - 1) return null;

                  const from = data;
                  const to = path[index + 1];
                  const zoneId = mapOptions.find(
                    (item) => item.id === zoneState
                  )?.walkway_id;

                  if (from.space_id === zoneId && to.space_id === zoneId) {
                    const { x: x_from, y: y_from } = projectionMapping(from);
                    const { x: x_to, y: y_to } = projectionMapping(to);
                    return (
                      <line
                        key={index}
                        x1={x_from}
                        y1={y_from}
                        x2={x_to}
                        y2={y_to}
                        stroke={'red'}
                        strokeWidth={5}
                      />
                    );
                  }
                  return null;
                })}
              </svg>
              {path.map((data: any, index: number) => {
                if (index === path.length - 1) return null;

                const from = data;
                const to = path[index + 1];
                const zoneId = mapOptions.find(
                  (item) => item.id === zoneState
                )?.walkway_id;

                if (from.space_id === zoneId && to.space_id !== zoneId) {
                  const buttonType =
                    from.category !== 'elevator' &&
                    from.category !== 'escalator'
                      ? 'exit'
                      : from.category === to.category
                      ? from.category
                      : 'exit';
                  const { x: x_from, y: y_from } = projectionMapping(from);
                  return (
                    <TransitPin
                      key={`from-${index}`}
                      type={buttonType}
                      left={`${x_from}px`}
                      top={`${y_from}px`}
                      translateX="-50%"
                      translateY="-50%"
                    />
                  );
                } else if (from.space_id !== zoneId && to.space_id === zoneId) {
                  const buttonType =
                    from.category !== 'elevator' &&
                    from.category !== 'escalator'
                      ? 'exit'
                      : from.category === to.category
                      ? from.category
                      : 'exit';
                  const { x: x_to, y: y_to } = projectionMapping(to);
                  return (
                    <TransitPin
                      key={`to-${index}`}
                      type={buttonType}
                      left={`${x_to}px`}
                      top={`${y_to}px`}
                      translateX="-50%"
                      translateY="-50%"
                    />
                  );
                } else {
                  return null;
                }
              })}
              {path.map((data: any, _index: number) => {
                if (data.id === pid) {
                  const { x, y } = projectionMapping(data);
                  return (
                    <NavigationMarker
                      key={'navigation-marker'}
                      top={`${y}px`}
                      left={`${x}px`}
                      translateX="-50%"
                      translateY="-50%"
                    />
                  );
                } else {
                  return null;
                }
              })}
            </>
          )}
        </animated.div>
      </MapContainer>
    </>
  );
};

const MapContainer = styled.div`
  width: 100%;
  height: 30dvh;
  flex: 1;
  z-index: 19;
  overflow: hidden;
  position: relative;
`;
const CurrentPinMarker = styled.img`
  width: 280px;
  height: 280px;
  transform: translate(-50%, -55%);
`;

export default XRNaviContainer;
